import { useEffect, useState, type RefObject } from 'react';

export interface ElementSize {
  width: number;
  height: number;
}

/**
 * Tracks an element's content box size via ResizeObserver.
 * Suited to resizable OS windows (not viewport media queries).
 */
export function useElementSize(
  ref: RefObject<HTMLElement | null>,
  initial: ElementSize = { width: 0, height: 0 },
): ElementSize {
  const [size, setSize] = useState<ElementSize>(initial);

  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }

    const updateFromRect = () => {
      const rect = node.getBoundingClientRect();
      setSize({ width: rect.width, height: rect.height });
    };

    if (typeof ResizeObserver === 'undefined') {
      updateFromRect();
      window.addEventListener('resize', updateFromRect);
      return () => window.removeEventListener('resize', updateFromRect);
    }

    updateFromRect();
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }
      const { width, height } = entry.contentRect;
      setSize({ width, height });
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref]);

  return size;
}

export default useElementSize;
